
import MomentUtils from '@date-io/moment';
const moment = new MomentUtils();

// Matches a trailing 'Z' or a numeric +HH:MM/-HH:MM offset - i.e. explicit timezone info.
const HAS_TZ_INFO = /(Z|[+-]\d{2}:?\d{2})$/;

// Self-detects real instant (Date/Z-string/already-aligned moment) vs naked/local value read
// as-is - needed so feeding an aligned value back into another helper (e.g. one setter
// adjusting the other) doesn't reproject it through the zone a second time.
const isAlignedInstant = (value) => typeof value?.isUTC === 'function' && typeof value?.toDate === 'function' && value.isUTC();
const hasTzInfo = (value) => value instanceof Date || (typeof value === 'string' && HAS_TZ_INFO.test(value)) || isAlignedInstant(value);

const alignDayBoundary = (value, edge, timeZoneId) => {
  const tz = timeZoneId || 'Europe/Vilnius';
  const dayStr = dayStrOf(value, tz);
  const [year, month, day] = dayStr.split('-').map(Number);
  const [hour, minute, second, ms] = edge === 'start' ? [0, 0, 0, 0] : [23, 59, 59, 999];
  return moment.date(zonedWallClockToUtc(year, month, day, hour, minute, second, ms, tz)).utc(0);
};

// "YYYY-MM-DD" for `date` as observed in `timeZoneId` (en-CA locale formats in that order).
// Cached per timeZoneId - a fresh Intl.DateTimeFormat per call is expensive in day-scanning loops.
const dayStrDtfCache = new Map();
const formatDateInZone = (date, timeZoneId) => {
  let dtf = dayStrDtfCache.get(timeZoneId);
  if (!dtf) {
    dtf = new Intl.DateTimeFormat('en-CA', { timeZone: timeZoneId });
    dayStrDtfCache.set(timeZoneId, dtf);
  }
  return dtf.format(date);
};

// Same self-detection as hasTzInfo, but returns just the day string.
const dayStrOf = (value, timeZoneId) => {
  const isReal = hasTzInfo(value);
  const parsed = isReal ? moment.date(value).utc() : moment.date(value);
  return timeZoneId && isReal ? formatDateInZone(parsed.toDate(), timeZoneId) : parsed.format('YYYY-MM-DD');
};

// Each end resolved independently via alignDayBoundary since a range's two ends can come from mixed wire formats.
const isWithinExcludedRange = (dateStr, range, timeZoneId) => {
  const fromStr = dayStrOf(alignDayBoundary(range.from, 'start', timeZoneId), timeZoneId);
  const toStr = dayStrOf(alignDayBoundary(range.to, 'end', timeZoneId), timeZoneId);
  return dateStr >= fromStr && dateStr <= toStr;
};

const makeIsExcluded = (excludesList, timeZoneId) => {
  const list = excludesList || [];
  return (day) => list.some((range) => isWithinExcludedRange(day, range, timeZoneId));
};

// Falls back to startValue if nothing opens up within maxDays, rather than an unverified guess
// - the resulting selection is then rejected by the same server-side validation, with a clear reason.
const firstAvailableDayBoundaries = (startValue, excludesList, timeZoneId, maxDays = 366) => {
  const startDayStr = dayStrOf(alignDayBoundary(startValue, 'start', timeZoneId), timeZoneId);
  const isExcluded = makeIsExcluded(excludesList, timeZoneId);
  let dayStr = startDayStr;
  let found = !isExcluded(dayStr);
  for (let i = 0; i < maxDays && !found; i++) {
    dayStr = moment.date(dayStr).add(1, 'day').format('YYYY-MM-DD');
    found = !isExcluded(dayStr);
  }
  const resultDayStr = found ? dayStr : startDayStr;
  return { from: alignDayBoundary(resultDayStr, 'start', timeZoneId), to: alignDayBoundary(resultDayStr, 'end', timeZoneId) };
};

// skipWeekends: a weekend day doesn't itself end the run - the scan continues past it looking
// for the next real excluded boundary.
const lastAvailableDayBeforeExcluded = (startValue, excludesList, timeZoneId, skipWeekends, maxDays = 366) => {
  const isExcluded = makeIsExcluded(excludesList, timeZoneId);
  let dayStr = dayStrOf(alignDayBoundary(startValue, 'start', timeZoneId), timeZoneId);
  let lastAvailable = dayStr;
  for (let i = 0; i < maxDays; i++) {
    const nextDayStr = moment.date(dayStr).add(1, 'day').format('YYYY-MM-DD');
    if (isExcluded(nextDayStr)) {
      break;
    }
    const isWeekend = [0, 6].includes(moment.date(nextDayStr).day());
    if (!skipWeekends || !isWeekend) {
      lastAvailable = nextDayStr;
    }
    dayStr = nextDayStr;
  }
  return alignDayBoundary(lastAvailable, 'end', timeZoneId);
};

// Cached per timeZoneId - a fresh Intl.DateTimeFormat per call turned a 366-day scan into a multi-second main-thread block.
const wallClockDtfCache = new Map();
const getWallClockDtf = (timeZoneId) => {
  let dtf = wallClockDtfCache.get(timeZoneId);
  if (!dtf) {
    dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZoneId,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    wallClockDtfCache.set(timeZoneId, dtf);
  }
  return dtf;
};

const zonedWallClockToUtc = (year, month, day, hour, minute, second, ms, timeZoneId) => {
  const targetMs = Date.UTC(year, month - 1, day, hour, minute, second, ms);
  const dtf = getWallClockDtf(timeZoneId);
  const offsetMsAt = (utcMs) => {
    const parts = dtf.formatToParts(new Date(utcMs));
    const part = (type) => parseInt(parts.find((p) => p.type === type).value, 10);
    const shownAsUtcMs = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour') % 24, part('minute'), part('second'), ms);
    return shownAsUtcMs - utcMs;
  };
  // Two passes: the offset used to correct the guess is sampled at the guess itself, which can
  // differ from the offset in effect at the corrected instant if a DST transition falls between
  // them. Re-sampling at the corrected instant converges for standard 1-hour DST shifts.
  let resultMs = targetMs - offsetMsAt(targetMs);
  resultMs = targetMs - offsetMsAt(resultMs);
  return new Date(resultMs);
};

const wallClockMomentToUtc = (wallClockMoment, timeZoneId) => {
  const m = moment.date(wallClockMoment);
  if (!timeZoneId) return m.toDate();
  return zonedWallClockToUtc(m.year(), m.month() + 1, m.date(), m.hours(), m.minutes(), m.seconds(), m.milliseconds(), timeZoneId);
};

// Same self-detection as dayStrOf. Pass a Date to force the real-instant path regardless of
// string shape (e.g. after resolving a source's own "naked means UTC" convention upstream).
const zonedInstantToWallClock = (value, timeZoneId) => {
  const isReal = hasTzInfo(value);
  if (!isReal || !timeZoneId) return moment.date(value);
  const date = value instanceof Date ? value : moment.date(value).toDate();
  const dtf = getWallClockDtf(timeZoneId);
  const parts = dtf.formatToParts(date);
  const part = (type) => parseInt(parts.find((p) => p.type === type).value, 10);
  return moment.date([part('year'), part('month') - 1, part('day'), part('hour') % 24, part('minute'), part('second')]);
};

// Computed client-side (no server round trip needed) by feeding new Date() through zonedInstantToWallClock.
const nowInZone = (timeZoneId) => zonedInstantToWallClock(new Date(), timeZoneId);

// Branch depends only on isLegacyNakedContract, never on timeZoneId's presence - an unresolved
// territory on a legacy connection still needs a naked string (best-guess zone), since a real
// instant is misread wholesale by an API with no tz concept at all.
// TODO(old-api-support): drop the isLegacyNakedContract param and naked branch once every deployed
// API sends real UTC-Z dates.
const formatBoundaryForWire = (momentBoundary, edge, timeZoneId, isLegacyNakedContract) => {
  if (!isLegacyNakedContract) {
    return momentBoundary.toISOString();
  }
  const dayStr = formatDateInZone(momentBoundary.toDate(), timeZoneId || 'Europe/Vilnius');
  const timeStr = edge === 'start' ? '00:00:00.000' : '23:59:59.999';
  return `${dayStr}T${timeStr}`;
};

const resolveTimeSpan = (timespan) => {
  let resolvedTimeSpan = {}
  let arrTimespan = timespan.split('.')
  const arrTime = arrTimespan.slice(-1)[0].split(':')
  if (arrTimespan.length === 2) {
    resolvedTimeSpan['days'] = parseInt(arrTimespan[0])
  }
  resolvedTimeSpan['hours'] = parseInt(arrTime[0])
  resolvedTimeSpan['minutes'] = parseInt(arrTime[1])
  resolvedTimeSpan['seconds'] = parseInt(arrTime[2])
  return resolvedTimeSpan;
}

const resolveMinMaxDates = (periods, datefrom, dateto, id, timeZoneId) => {
  if (periods) {
    periods = periods.filter((x) => x.id !== id);
  }

  // Compared via alignDayBoundary (not raw string order, since wire formats can differ) - each
  // boundary aligned once and reused, not recomputed per comparison.
  const alignedDateFrom = alignDayBoundary(datefrom, 'start', timeZoneId);
  const alignedDateTo = alignDayBoundary(dateto, 'end', timeZoneId);
  const periodsByEnd = periods.map((x) => ({ raw: x, aligned: alignDayBoundary(x.to, 'end', timeZoneId) }));
  const periodsByStart = periods.map((x) => ({ raw: x, aligned: alignDayBoundary(x.from, 'start', timeZoneId) }));
  let from = periodsByEnd.reduce(
    (best, x) => (x.aligned.isBefore(alignedDateFrom) && (!best || x.aligned.isAfter(best.aligned)) ? x : best),
    null
  )?.raw.to;
  let to = periodsByStart.reduce(
    (best, x) => (x.aligned.isAfter(alignedDateTo) && (!best || x.aligned.isBefore(best.aligned)) ? x : best),
    null
  )?.raw.from;
  let minDate = alignDayBoundary(new Date(), 'start', timeZoneId);
  let maxDate = null;
  if (from) {
    let fromDate = alignDayBoundary(from, 'start', timeZoneId).add(1, 'days');
    if (minDate < fromDate) {
      minDate = fromDate;
    }
  }
  if (to) {
    let toDate = alignDayBoundary(to, 'end', timeZoneId).add(-1, 'days');
    maxDate = `${dayStrOf(toDate, timeZoneId)}T23:59:59.999`;
  }
  minDate = `${dayStrOf(minDate, timeZoneId)}T00:00:00.000`;
  return { minDate: minDate, maxDate: maxDate };
}

export {
  resolveTimeSpan,
  resolveMinMaxDates,
  alignDayBoundary,
  formatBoundaryForWire,
  formatDateInZone,
  dayStrOf,
  isWithinExcludedRange,
  firstAvailableDayBoundaries,
  lastAvailableDayBeforeExcluded,
  HAS_TZ_INFO,
  hasTzInfo,
  wallClockMomentToUtc,
  zonedInstantToWallClock,
  nowInZone,
};